import { z } from 'zod';
import { dateInputSchema, isoDateStringSchema, objectIdSchema } from './common.js';
import { callDirectionSchema, callOutcomeSchema } from './sales-vocabulary.js';
import { followUpQueueSchema, taskSchema } from './tasks.js';

/**
 * A customer's notes and calls, and the follow-ups queue served from their tasks. The tasks
 * themselves are `tasks.ts`.
 *
 * The rules differ on purpose: a note is editable and soft-deleted, a call is append-only, and a
 * task's `open` status is what the follow-up date is derived from.
 */

/** The longest a note (or a call's notes) may be. */
export const NOTE_BODY_MAX_LENGTH = 2000;
export const CALL_SUBJECT_MAX_LENGTH = 120;
export const CALL_NOTES_MAX_LENGTH = NOTE_BODY_MAX_LENGTH;
/** Four hours. A longer figure is a typo, not a call. */
export const CALL_MAX_DURATION_SECONDS = 4 * 60 * 60;

/* -------------------------------------------------------------------------------------------
 * Notes — what the shop knows about somebody. Editable; `editedAt` says so. Soft-deleted.
 * ---------------------------------------------------------------------------------------- */

/**
 * A note is a STANDING OBSERVATION — "prefers mornings", "budget is about 8k". It is not a record
 * of contact: that is a call, which is append-only. Notes are revised, so what protects the
 * record is that revision is VISIBLE: `editedAt` is stamped on every change and the screen says
 * "edited". No title: the list renders the first line of the body prominently instead.
 */
export const createNoteSchema = z.object({
  body: z.string().trim().min(1, 'Write the note').max(NOTE_BODY_MAX_LENGTH),
});
export type CreateNoteInput = z.input<typeof createNoteSchema>;
export const updateNoteSchema = createNoteSchema;
export type UpdateNoteInput = z.input<typeof updateNoteSchema>;

/**
 * One of a customer's notes. History carried over by the one-time data migration arrives as
 * ordinary notes — a logged WhatsApp message reads "[WhatsApp] …".
 */
export const customerNoteSchema = z.object({
  id: objectIdSchema,
  customerId: objectIdSchema,
  body: z.string(),
  authorUserId: z.string().nullable(),
  authorName: z.string().nullable(),
  createdAt: isoDateStringSchema,
  /** Null until somebody changes the body. */
  editedAt: isoDateStringSchema.nullable(),
});
export type CustomerNote = z.infer<typeof customerNoteSchema>;

export const customerNoteListSchema = z.object({ items: z.array(customerNoteSchema) });
export type CustomerNoteList = z.infer<typeof customerNoteListSchema>;

/* -------------------------------------------------------------------------------------------
 * Calls — what happened. Append-only: no update, no delete, anywhere.
 * ---------------------------------------------------------------------------------------- */

/** How long a call lasted, in seconds. */
export const callDurationSecondsSchema = z
  .number()
  .int('Duration must be a whole number of seconds')
  .min(0)
  .max(CALL_MAX_DURATION_SECONDS);

/**
 * Logging one. A call that did not connect has no duration — a "no answer" that lasted four
 * minutes is a typo, and refusing it keeps talk-time figures honest.
 */
export const createCallSchema = z
  .object({
    /** What the call was about, in the rep's own words. */
    subject: z.string().trim().min(1, 'Name the call').max(CALL_SUBJECT_MAX_LENGTH),
    direction: callDirectionSchema,
    outcome: callOutcomeSchema,
    /** When it happened. Defaults to now, server-side, when the client omits it. */
    occurredAt: dateInputSchema.optional(),
    durationSeconds: callDurationSecondsSchema.nullish(),
    notes: z.string().trim().max(CALL_NOTES_MAX_LENGTH).nullish(),
  })
  .refine(
    (call) =>
      call.outcome === 'reached' ||
      call.durationSeconds === null ||
      call.durationSeconds === undefined ||
      call.durationSeconds === 0,
    {
      message: 'A call that did not connect has no duration',
      path: ['durationSeconds'],
    },
  );
export type CreateCallInput = z.input<typeof createCallSchema>;

export const customerCallSchema = z.object({
  id: objectIdSchema,
  customerId: objectIdSchema,
  subject: z.string(),
  direction: callDirectionSchema,
  outcome: callOutcomeSchema,
  occurredAt: isoDateStringSchema,
  /** Seconds. Null on a call that never connected. */
  durationSeconds: z.number().int().nullable(),
  notes: z.string().nullable(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type CustomerCall = z.infer<typeof customerCallSchema>;

export const customerCallListSchema = z.object({ items: z.array(customerCallSchema) });
export type CustomerCallList = z.infer<typeof customerCallListSchema>;

/** Ticking a task off and logging the call that closed it, in one request. The call is optional. */
export const completeTaskSchema = z.object({
  call: createCallSchema.optional(),
});
export type CompleteTaskInput = z.input<typeof completeTaskSchema>;

export const customerTaskCompletionSchema = z.object({
  task: taskSchema,
  call: customerCallSchema.nullable(),
});
export type CustomerTaskCompletion = z.infer<typeof customerTaskCompletionSchema>;

/* -------------------------------------------------------------------------------------------
 * The follow-ups queue, served from customers' tasks
 * ---------------------------------------------------------------------------------------- */

/**
 * `GET /customers/follow-ups` — the Follow-ups page and the header menu. Every row is a customer's
 * open task with the customer's name and number (`subjectName` / `subjectPhone`, `subjectKind`
 * always `customer`), so the rows parse as `followUpQueueItemSchema`. Counts are drawn from the shop's day boundary:
 * `overdue` is before today began, `dueToday` is today, `upcoming` is after today.
 */
export const customerFollowUpQueueSchema = followUpQueueSchema.extend({
  upcoming: z.number().int(),
});
export type CustomerFollowUpQueue = z.infer<typeof customerFollowUpQueueSchema>;
